'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ChangeEvent, FormEvent } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { InputField } from '@/components/ui/input-field';
import { Label } from '@/components/ui/label';
import { SelectField } from '@/components/ui/select-field';
interface TransferInputStepProps {
  selectedWalletId: string;
  walletLabel: string;
  showWalletPicker: boolean;
  walletItems: {
    value: string;
    label: string;
  }[];
  onWalletChange: (walletId: string) => void;
  recipient: string;
  onRecipientChange: (value: string) => void;
  amount: string;
  onAmountChange: (value: string) => void;
  inputError: string | null;
  /** Pre-built "available balance" caption (USDC + formatted-currency). */
  availableBalanceLabel: string;
  onMaxAmount: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

/**
 * Step 1 of the transfer wizard: pick a source wallet, enter a recipient
 * address + amount. The `transfer-funds-form` id is shared with the footer's
 * submit button (rendered by the parent SilkSheet `footer`). Purely
 * presentational — all state, validation, and submission live in the parent
 * `TransferFunds`.
 */
export function TransferInputStep(t0) {
  const $ = _c(56);
  const {
    selectedWalletId,
    walletLabel,
    showWalletPicker,
    walletItems,
    onWalletChange,
    recipient,
    onRecipientChange,
    amount,
    onAmountChange,
    inputError,
    availableBalanceLabel,
    onMaxAmount,
    onSubmit
  } = t0;
  const t = useTranslations("wallet.transfer");
  let t1;
  if ($[0] !== onWalletChange || $[1] !== selectedWalletId || $[2] !== showWalletPicker || $[3] !== t || $[4] !== walletItems || $[5] !== walletLabel) {
    t1 = showWalletPicker ? <SelectField id="transfer-from" label={t("from_label")} value={selectedWalletId} onValueChange={onWalletChange} items={walletItems} /> : <div className="flex flex-col gap-1.5"><Label>{t("from_label")}</Label><div className="rounded-control border bg-background-primary px-3 py-2.5 text-body font-medium">{walletLabel}</div></div>;
    $[0] = onWalletChange;
    $[1] = selectedWalletId;
    $[2] = showWalletPicker;
    $[3] = t;
    $[4] = walletItems;
    $[5] = walletLabel;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t) {
    t2 = t("recipient_label");
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== onRecipientChange) {
    t3 = e => {
      onRecipientChange(e.target.value);
    };
    $[9] = onRecipientChange;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t) {
    t4 = t("recipient_placeholder");
    $[11] = t;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = inputError !== null ? true : undefined;
  const t6 = inputError !== null ? "transfer-error" : undefined;
  let t7;
  if ($[13] !== recipient || $[14] !== t2 || $[15] !== t3 || $[16] !== t4 || $[17] !== t5 || $[18] !== t6) {
    t7 = <InputField id="transfer-recipient" label={t2} type="text" value={recipient} onChange={t3} placeholder={t4} autoComplete="off" spellCheck={false} data-dd-privacy="mask" aria-invalid={t5} aria-describedby={t6} />;
    $[13] = recipient;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t) {
    t8 = t("amount_label");
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t8) {
    t9 = <Label htmlFor="transfer-amount">{t8}</Label>;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== onAmountChange) {
    t10 = e_0 => {
      onAmountChange(e_0.target.value);
    };
    $[24] = onAmountChange;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t) {
    t11 = t("amount_placeholder");
    $[26] = t;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const t12 = inputError !== null ? true : undefined;
  const t13 = inputError !== null ? "transfer-error" : undefined;
  let t14;
  if ($[28] !== amount || $[29] !== t10 || $[30] !== t11 || $[31] !== t12 || $[32] !== t13) {
    t14 = <InputField id="transfer-amount" type="number" value={amount} onChange={t10} placeholder={t11} min={0} step="any" autoComplete="off" aria-invalid={t12} aria-describedby={t13} />;
    $[28] = amount;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t) {
    t15 = t("max_button");
    $[34] = t;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== onMaxAmount || $[37] !== t15) {
    t16 = <AnimatedButton type="button" variant="secondary" onClick={onMaxAmount} className="h-10 shrink-0">{t15}</AnimatedButton>;
    $[36] = onMaxAmount;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t14 || $[40] !== t16) {
    t17 = <div className="flex gap-2">{t14}{t16}</div>;
    $[39] = t14;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== availableBalanceLabel) {
    t18 = <p className="text-caption text-foreground-secondary">{availableBalanceLabel}</p>;
    $[42] = availableBalanceLabel;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t17 || $[45] !== t18 || $[46] !== t9) {
    t19 = <div className="flex flex-col gap-1.5">{t9}{t17}{t18}</div>;
    $[44] = t17;
    $[45] = t18;
    $[46] = t9;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== inputError) {
    t20 = inputError !== null && <p id="transfer-error" role="alert" className="text-body text-feedback-error-content">{inputError}</p>;
    $[48] = inputError;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== onSubmit || $[51] !== t1 || $[52] !== t19 || $[53] !== t20 || $[54] !== t7) {
    t21 = <form id="transfer-funds-form" onSubmit={onSubmit} className="flex flex-col gap-4">{t1}{t7}{t19}{t20}</form>;
    $[50] = onSubmit;
    $[51] = t1;
    $[52] = t19;
    $[53] = t20;
    $[54] = t7;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  return t21;
}